/**
 * ShrineVisual (O-025) — renders one dungeon-entrance shrine from the package's
 * renderer-neutral scene plan. Placeholder-primitive first; when a model source is
 * given, the owner-approved `dungeon-entrance-kit-v1` GLB for the profile (approved
 * 2026-07-21, integrated H-1322) replaces the STRUCTURAL primitives once it loads.
 * The glow parts stay code-built: the GLB's own portal slab and interact ring are
 * hidden, so the emissive door, halos, omen icons and ring keep driving the read.
 * A load failure leaves the primitives — the shrine never disappears.
 *
 * Glow language (owner spec): a GENERAL difficulty glow around the shrine — soft,
 * additive, colour-graded by difficulty rank — with the portal DOOR carrying the
 * extremely strong version, appearing to be the SOURCE the area glow radiates
 * from, at intensities that stay under bloom-blowout (AD-194 AgX pipeline).
 */

import * as THREE from 'three';

import type {
  DungeonEntranceScenePart,
  DungeonEntranceScenePlan,
  DungeonEntranceSite,
  DungeonShrineProfile,
} from '@great-cloud/procedural-dungeons';

import { disposeObject3D } from '../assets/gpuOwnership.js';
import { omenIconTexture } from './omenIcons.js';

const QUARTER = Math.PI / 2;

/** The served pack holding one approved GLB per shrine profile, keyed by profile id. */
export const SHRINE_KIT_PACK = 'dungeon-entrance-kit-v1';

/** Resolves a profile's approved shrine model; `null` keeps the primitives. */
export type ShrineModelSource = (profileId: string) => Promise<THREE.Object3D | null>;

/** Adapts a pack-loader lookup (`interior.ts` `LoaderFor`) into a shrine model source. */
export function shrineModelSource(
  loaderFor: (packId: string) => { instantiate(id: string): Promise<THREE.Object3D | null> } | null,
): ShrineModelSource {
  return (profileId) => loaderFor(SHRINE_KIT_PACK)?.instantiate(profileId) ?? Promise.resolve(null);
}

/** Primitive kinds the GLB replaces; the glow/marker kinds stay code-built. */
const STRUCTURAL_KINDS: ReadonlySet<DungeonEntranceScenePart['kind']> = new Set([
  'platform',
  'arch',
  'column',
  'buttress',
  'spire',
  'crown',
  'omen-panel',
]);

/** GLB materials whose surfaces the code-built door and ring already draw. */
const MODEL_HIDDEN_MATERIALS: ReadonlySet<string> = new Set(['portal-ready', 'interact-gold']);

/** Hides the model's portal slab + interact ring so the emissive door stays the source. */
export function prepareShrineModel(model: THREE.Object3D): THREE.Object3D {
  model.name = 'shrine-model';
  model.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    if (materials.some((material) => MODEL_HIDDEN_MATERIALS.has(material.name))) {
      mesh.visible = false;
      return;
    }
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  });
  return model;
}

type PortalState = DungeonEntranceScenePlan['portalState'];

/** Difficulty accent used by BOTH glows so the door reads as the source. */
function accentColour(profile: DungeonShrineProfile, portal: PortalState): string {
  if (portal === 'sealed') return profile.materials.portalSealed;
  if (portal === 'ready') return profile.materials.portalReady;
  return profile.materials.portalQuiet;
}

function radialGlowTexture(): THREE.Texture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  const gradient = ctx.createRadialGradient(64, 64, 4, 64, 64, 62);
  gradient.addColorStop(0, 'rgba(255,255,255,0.85)');
  gradient.addColorStop(0.35, 'rgba(255,255,255,0.28)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

let sharedGlow: THREE.Texture | null = null;
function glowTexture(): THREE.Texture {
  if (!sharedGlow) sharedGlow = radialGlowTexture();
  return sharedGlow;
}

export class ShrineVisual {
  readonly group = new THREE.Group();
  private built: THREE.Object3D[] = [];
  private portalMaterial: THREE.MeshStandardMaterial | null = null;
  private portalHalo: THREE.Sprite | null = null;
  private areaHalo: THREE.Sprite | null = null;
  private portalLight: THREE.PointLight | null = null;
  private markerSprites: THREE.Sprite[] = [];
  private ringMaterial: THREE.MeshBasicMaterial | null = null;
  private portal: PortalState = 'quiet';
  private portalIntensity = 0.5;
  private revealRadius = 120;
  /** The approved GLB once loaded; owned by the pack cache, so never disposed here. */
  private model: THREE.Object3D | null = null;
  private modelPending = false;
  private disposed = false;

  constructor(
    private readonly site: DungeonEntranceSite,
    private readonly profile: DungeonShrineProfile,
    loadModel: ShrineModelSource | null = null,
  ) {
    this.group.name = `shrine:${site.id}`;
    this.group.position.set(site.position.x, site.position.y, site.position.z);
    this.group.rotation.y = site.rotationQuarterTurns * QUARTER;
    if (loadModel) this.requestModel(loadModel);
  }

  /**
   * Asks for the approved GLB. Idempotent while a request is in flight or a model is
   * in; a `null` answer or a failure keeps the primitives and leaves a later call free
   * to retry — the content pack that declares the kit arrives AFTER the helloAck that
   * builds the shrines, so the caller re-asks from `onContentPack` (Game.ts).
   */
  requestModel(source: ShrineModelSource): void {
    if (this.model || this.modelPending || this.disposed) return;
    this.modelPending = true;
    source(this.profile.id)
      .then((model) => {
        if (model && !this.disposed && !this.model) this.adoptModel(model);
      })
      .catch(() => {
        // The primitives stay: a missing or broken GLB must never hide the shrine.
      })
      .finally(() => {
        this.modelPending = false;
      });
  }

  /** True once the approved GLB stands in for the structural primitives. */
  get usesModel(): boolean {
    return this.model !== null;
  }

  private adoptModel(model: THREE.Object3D): void {
    this.model = prepareShrineModel(model);
    this.group.add(this.model);
    this.applyModelVisibility();
  }

  private applyModelVisibility(): void {
    if (!this.model) return;
    for (const object of this.built) {
      if (object.userData.shrineStructural === true) object.visible = false;
    }
  }

  /**
   * The plan's `portalState` drives every glow: `ready` for a primed techdemo
   * site, and for a server-advertised entrance, which is always enterable
   * (`serverEntranceShrine`, run.ts).
   */
  rebuild(plan: DungeonEntranceScenePlan): void {
    this.clearBuilt();
    this.portal = plan.portalState;
    this.portalIntensity = plan.portalIntensity;
    this.revealRadius = plan.previewRevealRadius;
    const accent = new THREE.Color(accentColour(this.profile, this.portal));
    const ready = this.portal === 'ready';

    for (const part of plan.parts) {
      const object = this.buildPart(part, accent, ready);
      if (object) {
        if (STRUCTURAL_KINDS.has(part.kind)) object.userData.shrineStructural = true;
        this.built.push(object);
        this.group.add(object);
      }
    }
    this.applyModelVisibility();

    // GENERAL area glow: one large, faint additive halo hugging the ground — reads
    // as light the portal casts into the clearing (difficulty-tinted, rank-scaled).
    const areaMaterial = new THREE.SpriteMaterial({
      map: glowTexture(),
      color: accent,
      transparent: true,
      opacity: ready ? 0.16 : 0.08,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.areaHalo = new THREE.Sprite(areaMaterial);
    const reach = this.profile.footprintRadius * (2.2 + this.profile.rank * 0.55);
    this.areaHalo.scale.set(reach, reach * 0.55, 1);
    this.areaHalo.position.set(0, 0.9 + this.profile.rank * 0.25, 0);
    this.group.add(this.areaHalo);
    this.built.push(this.areaHalo);
  }

  private buildPart(
    part: DungeonEntranceScenePart,
    accent: THREE.Color,
    ready: boolean,
  ): THREE.Object3D | null {
    const colour = new THREE.Color(this.profile.materials[part.materialKey] ?? '#888888');
    switch (part.kind) {
      case 'portal': {
        // The door: strong emissive core + a bright halo + a real light so the
        // surrounding stone visibly receives the radiance.
        this.portalMaterial = new THREE.MeshStandardMaterial({
          color: colour,
          emissive: accent,
          emissiveIntensity: ready ? 2.6 * this.portalIntensity : 0.35,
          roughness: 0.35,
          metalness: 0.05,
          transparent: true,
          opacity: 0.94,
        });
        const door = new THREE.Mesh(
          new THREE.PlaneGeometry(part.scale.x, part.scale.y),
          this.portalMaterial,
        );
        door.position.set(part.position.x, part.position.y, part.position.z);
        door.name = part.id;
        const holo = new THREE.SpriteMaterial({
          map: glowTexture(),
          color: accent,
          transparent: true,
          opacity: ready ? 0.5 : 0.12,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });
        this.portalHalo = new THREE.Sprite(holo);
        this.portalHalo.scale.set(part.scale.x * 2.4, part.scale.y * 1.7, 1);
        this.portalHalo.position.copy(door.position);
        this.portalLight = new THREE.PointLight(
          accent,
          ready ? 2.2 * this.portalIntensity : 0.3,
          this.profile.footprintRadius * 4.5,
          1.8,
        );
        this.portalLight.position.set(
          part.position.x,
          part.position.y + 0.4,
          part.position.z + 0.8,
        );
        const cluster = new THREE.Group();
        cluster.add(door, this.portalHalo, this.portalLight);
        return cluster;
      }
      case 'icon-slot': {
        const sprite = new THREE.Sprite(
          new THREE.SpriteMaterial({
            map: omenIconTexture(part.symbolKey ?? null, part.bossMarked === true),
            transparent: true,
            opacity: part.symbolKey ? 0.96 : 0.35,
            depthWrite: false,
          }),
        );
        sprite.scale.set(part.scale.x * 1.35, part.scale.x * 1.35, 1);
        sprite.position.set(part.position.x, part.position.y + 0.35, part.position.z + 0.05);
        sprite.name = part.id;
        this.markerSprites.push(sprite);
        return sprite;
      }
      case 'interact-anchor': {
        this.ringMaterial = new THREE.MeshBasicMaterial({
          color: new THREE.Color(this.profile.materials.interact),
          transparent: true,
          opacity: 0.0,
          side: THREE.DoubleSide,
        });
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(part.scale.x * 0.72, part.scale.x, 40),
          this.ringMaterial,
        );
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(part.position.x, part.position.y + 0.02, part.position.z);
        ring.name = part.id;
        return ring;
      }
      case 'omen-panel':
      case 'platform':
      case 'arch':
      case 'column':
      case 'buttress':
      case 'spire':
      case 'crown': {
        const geometry =
          part.primitive === 'cylinder'
            ? new THREE.CylinderGeometry(part.scale.x * 0.5, part.scale.x * 0.55, part.scale.y, 10)
            : new THREE.BoxGeometry(part.scale.x, part.scale.y, part.scale.z);
        const mesh = new THREE.Mesh(
          geometry,
          new THREE.MeshStandardMaterial({ color: colour, roughness: 0.87, metalness: 0.04 }),
        );
        mesh.position.set(part.position.x, part.position.y + part.scale.y * 0, part.position.z);
        // Scene-plan positions are CENTRES for boxes except platforms/arches authored
        // base-anchored: the package emits centre positions uniformly — keep as-is.
        mesh.rotation.y = part.rotationY;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.name = part.id;
        return mesh;
      }
      default:
        return null;
    }
  }

  /** Cheap per-frame pulse + distance-based marker/ring fades. */
  animate(nowMs: number, playerX: number, playerZ: number): void {
    const pulse = 0.5 + 0.5 * Math.sin(nowMs / 640);
    if (this.portalMaterial && this.portal === 'ready') {
      this.portalMaterial.emissiveIntensity = (2.1 + 1.1 * pulse) * this.portalIntensity;
    }
    if (this.portalHalo && this.portal === 'ready') {
      this.portalHalo.material.opacity = 0.38 + 0.2 * pulse;
    }
    if (this.portalLight) {
      this.portalLight.intensity =
        (this.portal === 'ready' ? 1.9 + 0.8 * pulse : 0.3) * this.portalIntensity;
    }
    const dx = playerX - this.site.position.x;
    const dz = playerZ - this.site.position.z;
    const distance = Math.hypot(dx, dz);
    const markerAlpha =
      this.portal === 'ready'
        ? THREE.MathUtils.clamp(1.35 - distance / this.revealRadius, 0, 1)
        : 0.3;
    for (const sprite of this.markerSprites) {
      sprite.material.opacity = sprite.material.map ? Math.min(0.96, 0.2 + markerAlpha) : 0.2;
    }
    if (this.ringMaterial) {
      const near = THREE.MathUtils.clamp(
        1 - distance / (this.site.radii.interactionRadius * 2.5),
        0,
        1,
      );
      this.ringMaterial.opacity = this.portal === 'ready' ? 0.15 + 0.65 * near : 0;
    }
  }

  private clearBuilt(): void {
    for (const object of this.built) {
      this.group.remove(object);
      disposeObject3D(object); // H-1349: the kit GLB clone borrows the pack cache's buffers
    }
    this.built = [];
    this.markerSprites = [];
    this.portalMaterial = null;
    this.portalHalo = null;
    this.areaHalo = null;
    this.portalLight = null;
    this.ringMaterial = null;
  }

  dispose(): void {
    this.disposed = true;
    this.clearBuilt();
    if (this.model) {
      this.group.remove(this.model);
      this.model = null;
    }
  }
}
